<form [formGroup]="chargeScheduleForm" (ngSubmit)="onSubmit()" (keydown.enter)="$event.preventDefault()" class="main-content mb-4 mx-lg-4 mx-md-4 mx-sm-0 px-sm-4 px-3">
    <ms-page-title />
    <ms-stripe-setup-notice />

    @if (timezoneWarningMessage && !dismissibleNotesService.isDismissed(timezoneWarningDismissKey)) {
        <div class="alert alert-warning alert-dismissible" role="alert">
            <fa-duotone-icon icon="circle-info" class="me-2" />
            <strong>Note: </strong> <span [innerHTML]="timezoneWarningMessage"></span>
            <button type="button" class="btn-close" aria-label="Dismiss"
                (click)="dismissibleNotesService.dismiss(timezoneWarningDismissKey)"></button>
        </div>
    }
    <!-- Top Section: Two Column Layout -->
    <div class="row g-4 mb-5">
        <!-- Left Column -->
        <div class="col-md-6">
            <!-- Invoice Name -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'invoiceName')" [labelText]="'Invoice Name'" />
            <input 
                type="text" 
                id="invoiceName" 
                formControlName="invoiceName"
                [required]="isFieldRequired(chargeScheduleForm, 'invoiceName')"
                maxlength="250"
                class="form-control"
                [class.is-invalid]="chargeScheduleForm.get('invoiceName')?.invalid && chargeScheduleForm.get('invoiceName')?.touched"
                [class.is-valid]="chargeScheduleForm.get('invoiceName')?.valid && chargeScheduleForm.get('invoiceName')?.touched && chargeScheduleForm.get('invoiceName')?.value !== ''" />
            @if(chargeScheduleForm.get('invoiceName')?.invalid && chargeScheduleForm.get('invoiceName')?.touched) {
                <div class="error-messages">
                    @if(chargeScheduleForm.get('invoiceName')?.errors?.['required']) {
                        <p class="text-danger text-start pt-1 mb-0">Invoice name is required</p>
                    }
                    @if(chargeScheduleForm.get('invoiceName')?.errors?.['maxlength']) {
                        <p class="text-danger text-start pt-1 mb-0">Invoice name cannot exceed 250 characters</p>
                    }
                </div>
            }

            <!-- Amount -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'amount')" [labelText]="'Amount'" class="mt-3" />
            <ms-amount-input formControlName="amount" />

            <!-- Invoice Schedule Description -->
            <ms-input-label [labelText]="'Invoice Schedule Description'" [tooltip]="InvoiceScheduleHelpText.Description" class="mt-3" />
            <input 
                type="text" 
                id="description" 
                formControlName="description"
                maxlength="250"
                class="form-control"
                [class.is-valid]="chargeScheduleForm.get('description')?.valid && chargeScheduleForm.get('description')?.touched && chargeScheduleForm.get('description')?.value !== ''" />
            @if(chargeScheduleForm.get('description')?.errors?.['maxlength'] && chargeScheduleForm.get('description')?.touched) {
                <p class="text-danger text-start pt-1 mb-0">Description cannot exceed 250 characters</p>
            }

            <!-- Activate Charge Toggle -->
            <div class="d-flex align-items-center mt-3">
                <div class="form-check form-switch fs-5">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="activateCharge"
                        formControlName="activateCharge">
                    <label class="form-check-label fs-6" for="activateCharge">
                        Activate Charge
                    </label>
                    <fa-duotone-icon icon="circle-info" secondaryOpacity="0.2" class="text-primary ms-2 fs-6" transform="grow-4"
                        [msTooltip]="InvoiceScheduleHelpText.ActivateCharge" />
                </div>
            </div>

            <!-- Service Fees Toggle -->
            <!-- TODO: DEV-2596 - Jake - Revisit when we allow organizations to pay service fees -->
            <!-- <div class="d-flex align-items-center mt-3">
                <div class="form-check form-switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="serviceFees"
                        formControlName="serviceFees">
                    <label class="form-check-label" for="serviceFees">
                        Member pays service fees
                    </label>
                </div>
            </div>
            @if (!chargeScheduleForm.get('serviceFees')?.value) {
                <p class="text-danger small ms-2 mb-0">
                    Organization receives amount of payment minus transaction processing fees
                </p>
            }
            @if (showRetroactiveFeeWarning()) {
                <div class="alert alert-warning d-flex align-items-start mt-2 mb-0 py-2" role="alert">
                    <fa-duotone-icon icon="triangle-exclamation" class="me-2 mt-1" secondaryOpacity="0.3" />
                    <small>{{ retroactiveFeeWarningMessage() }}</small>
                </div>
            } -->

            

            <!-- Income Type (Chart of Account) -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'revenueType')" [labelText]="'Income Type'" [tooltip]="InvoiceScheduleHelpText.IncomeType" class="mt-3" />
            <ms-chart-of-account-typeahead
                #revenueTypeTypeahead
                id="revenueType"
                formControlName="revenueType"
                [organizationId]="userSessionService.activeOrganization()?.organizationId"
                [categories]="[ChartOfAccountCategory.Revenue]"
                [invalid]="chargeScheduleForm.get('revenueType')?.invalid && chargeScheduleForm.get('revenueType')?.touched ? true : false" />
            @if (revenueTypeTypeahead.results().length === 0 && !revenueTypeTypeahead.isSearching()) {
                <p class="text-warning small mb-0 mt-1">
                    <fa-duotone-icon icon="triangle-exclamation" class="me-1" secondaryOpacity="0.3" />
                    {{ FormValidatorMessages.InvoiceScheduleRevenueTypeNoAccountsAvailable }}
                </p>
            } @else if (chargeScheduleForm.get('revenueType')?.invalid && chargeScheduleForm.get('revenueType')?.touched) {
                <div class="error-messages">
                    <p class="text-danger text-start pt-1 mb-0">Income type is required</p>
                </div>
            }

            <!-- Asset Type -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'assetType')" [labelText]="'Asset Type'" [tooltip]="InvoiceScheduleHelpText.AssetType" class="mt-3" />
            <ms-chart-of-account-typeahead
                #assetTypeTypeahead
                id="assetType"
                formControlName="assetType"
                [organizationId]="userSessionService.activeOrganization()?.organizationId"
                [categories]="[ChartOfAccountCategory.Assets]"
                [invalid]="chargeScheduleForm.get('assetType')?.invalid && chargeScheduleForm.get('assetType')?.touched ? true : false" />
            @if (assetTypeTypeahead.results().length === 0 && !assetTypeTypeahead.isSearching()) {
                <p class="text-warning small mb-0 mt-1">
                    <fa-duotone-icon icon="triangle-exclamation" class="me-1" secondaryOpacity="0.3" />
                    {{ FormValidatorMessages.InvoiceScheduleAssetTypeNoAccountsAvailable }}
                </p>
            } @else if (chargeScheduleForm.get('assetType')?.invalid && chargeScheduleForm.get('assetType')?.touched) {
                <div class="error-messages">
                    <p class="text-danger text-start pt-1 mb-0">Asset type is required</p>
                </div>
            }
        </div>

        <!-- Right Column -->
        <div class="col-md-6">
            <!-- Frequency -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'frequency')" [labelText]="'Frequency'" [tooltip]="InvoiceScheduleHelpText.Frequency" />
            <select 
                id="frequency" 
                formControlName="frequency"
                [required]="isFieldRequired(chargeScheduleForm, 'frequency')"
                class="form-select"
                [class.is-invalid]="chargeScheduleForm.get('frequency')?.invalid && chargeScheduleForm.get('frequency')?.touched"
                [class.is-valid]="chargeScheduleForm.get('frequency')?.valid && chargeScheduleForm.get('frequency')?.touched">
                @for (option of frequencyOptions; track option) {
                    <option [value]="option">{{ option }}</option>
                }
            </select>

            <!-- Custom Frequency (Conditional) -->
            @if(isCustomFrequency()) {
                <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'customFrequencyMonths')" [labelText]="'Custom Frequency'" class="mt-3" />
                <select
                    id="customFrequencyMonths"
                    formControlName="customFrequencyMonths"
                    aria-label="Custom Frequency"
                    [required]="isFieldRequired(chargeScheduleForm, 'customFrequencyMonths')"
                    class="form-select"
                    [class.is-invalid]="chargeScheduleForm.get('customFrequencyMonths')?.invalid && chargeScheduleForm.get('customFrequencyMonths')?.touched"
                    [class.is-valid]="chargeScheduleForm.get('customFrequencyMonths')?.valid && chargeScheduleForm.get('customFrequencyMonths')?.touched">
                    <option [ngValue]="null" disabled>Select a frequency</option>
                    @for (months of customFrequencyMonthsOptions; track months) {
                        <option [ngValue]="months">Every {{ months }} months</option>
                    }
                </select>
                @if(chargeScheduleForm.get('customFrequencyMonths')?.invalid && chargeScheduleForm.get('customFrequencyMonths')?.touched) {
                    <div class="error-messages">
                        @if(chargeScheduleForm.get('customFrequencyMonths')?.errors?.['required']) {
                            <p class="text-danger text-start pt-1 mb-0">Custom frequency is required</p>
                        }
                    </div>
                }
            }

            <!-- Issuance Date -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'issuanceDate')" [labelText]="'Issuance Date'" [tooltip]="InvoiceScheduleHelpText.IssuanceDate" class="mt-3" />
            <input 
                type="date" 
                id="issuanceDate" 
                formControlName="issuanceDate"
                [required]="isFieldRequired(chargeScheduleForm, 'issuanceDate')"
                class="form-control"
                [class.is-invalid]="chargeScheduleForm.get('issuanceDate')?.invalid && chargeScheduleForm.get('issuanceDate')?.touched"
                [class.is-valid]="chargeScheduleForm.get('issuanceDate')?.valid && chargeScheduleForm.get('issuanceDate')?.touched" />

            @if(chargeScheduleForm.get('issuanceDate')?.invalid && chargeScheduleForm.get('issuanceDate')?.touched) {
                <div class="error-messages">
                    @if(chargeScheduleForm.get('issuanceDate')?.errors?.['required']) {
                        <p class="text-danger text-start pt-1 mb-0">A valid issuance date is required</p>
                    }
                    @if(chargeScheduleForm.get('issuanceDate')?.errors?.['futureDate']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('issuanceDate')?.errors?.['futureDate'].message }}</p>
                    }
                </div>
            }
            @if(isIssuanceDateEndOfMonth() && !(chargeScheduleForm.get('frequency')?.value === ChargeScheduleFrequency.Once)) {
                <div class="alert alert-info d-flex align-items-center mt-2 mb-0 py-2" role="alert">
                    <fa-duotone-icon icon="circle-info" class="me-2" secondaryOpacity="0.2" size="lg" />
                    <small>This date will be treated as end of month</small>
                </div>
            }

            <!-- Due Date -->
            <ms-input-label [isRequired]="isFieldRequired(chargeScheduleForm, 'dueDate')" [labelText]="'Due Date'" class="mt-3" />
            <input 
                type="date" 
                id="dueDate" 
                formControlName="dueDate"
                [required]="isFieldRequired(chargeScheduleForm, 'dueDate')"
                class="form-control"
                [class.is-invalid]="chargeScheduleForm.get('dueDate')?.invalid && chargeScheduleForm.get('dueDate')?.touched"
                [class.is-valid]="chargeScheduleForm.get('dueDate')?.valid && chargeScheduleForm.get('dueDate')?.touched" />
            @if(chargeScheduleForm.get('dueDate')?.invalid && chargeScheduleForm.get('dueDate')?.touched) {
                <div class="error-messages">
                    @if(chargeScheduleForm.get('dueDate')?.errors?.['required']) {
                        <p class="text-danger text-start pt-1 mb-0">A valid due date is required</p>
                    }
                    @if(chargeScheduleForm.get('dueDate')?.errors?.['futureDate']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('dueDate')?.errors?.['futureDate'].message }}</p>
                    }
                    @if(chargeScheduleForm.get('dueDate')?.errors?.['dueDate']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('dueDate')?.errors?.['dueDate'].message }}</p>
                    }
                </div>
            }
            @if(isDueDateEndOfMonth() && !(chargeScheduleForm.get('frequency')?.value === ChargeScheduleFrequency.Once)) {
                <div class="alert alert-info d-flex align-items-center mt-2 mb-0 py-2" role="alert">
                    <fa-duotone-icon icon="circle-info" class="me-2" secondaryOpacity="0.2" size="lg" />
                    <small>This date will be treated as end of month</small>
                </div>
            }

            <!-- End Date (Conditional) -->
            @if(shouldShowEndDate()) {
                <ms-input-label [labelText]="'End Date'" class="mt-3" />
                <input 
                    type="date" 
                    id="endDate" 
                    formControlName="endDate"
                    class="form-control"
                    [class.is-invalid]="chargeScheduleForm.get('endDate')?.invalid && chargeScheduleForm.get('endDate')?.touched"
                    [class.is-valid]="chargeScheduleForm.get('endDate')?.valid && chargeScheduleForm.get('endDate')?.touched && chargeScheduleForm.get('endDate')?.value !== ''" />
                @if(chargeScheduleForm.get('endDate')?.invalid && chargeScheduleForm.get('endDate')?.touched) {
                    <div class="error-messages">
                        @if(chargeScheduleForm.get('endDate')?.errors?.['invalidDate']) {
                            <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('endDate')?.errors?.['invalidDate'].message }}</p>
                        }
                        @if(chargeScheduleForm.get('endDate')?.errors?.['futureDate']) {
                            <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('endDate')?.errors?.['futureDate'].message }}</p>
                        }
                        @if(chargeScheduleForm.get('endDate')?.errors?.['endDate']) {
                            <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('endDate')?.errors?.['endDate'].message }}</p>
                        }
                        @if(chargeScheduleForm.get('endDate')?.errors?.['endBeforeMonthEnd']) {
                            <p class="text-danger text-start pt-1 mb-0">{{ chargeScheduleForm.get('endDate')?.errors?.['endBeforeMonthEnd'].message }}</p>
                        }
                    </div>
                }
            }

            <!-- Invoice Instructions -->
            <ms-input-label [labelText]="'Invoice Instructions'" [tooltip]="InvoiceScheduleHelpText.InvoiceInstructions" class="mt-3" />
            <textarea 
                id="invoiceInstructions" 
                formControlName="invoiceInstructions"
                maxlength="250"
                rows="3"
                class="form-control"
                [class.is-valid]="chargeScheduleForm.get('invoiceInstructions')?.valid && chargeScheduleForm.get('invoiceInstructions')?.touched && chargeScheduleForm.get('invoiceInstructions')?.value !== ''"></textarea>
            @if(chargeScheduleForm.get('invoiceInstructions')?.errors?.['maxlength'] && chargeScheduleForm.get('invoiceInstructions')?.touched) {
                <p class="text-danger text-start pt-1 mb-0">Instructions cannot exceed 250 characters</p>
            }
        </div>
    </div>

    <!-- Late Fees (optional, applies to any schedule — one-time or recurring) -->
    <div class="mb-4">
        <ms-late-fees-section
            [dueDate]="dueDateValue() || undefined"
            [organizationId]="userSessionService.activeOrganization()?.organizationId">
        </ms-late-fees-section>
    </div>

    <!-- Form Action Buttons -->
    <div class="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3 align-items-stretch align-items-sm-center">
        <button type="button" class="btn btn-ms-secondary order-2 order-sm-1" (click)="onCancel()">
            Cancel
        </button>
        <button type="submit" class="btn btn-primary order-1 order-sm-2">
            @if(isSubmitting) {
                <div class="spinner-border text-light spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            } @else {
                Save
            }
        </button>
        <!-- Property Selection Validation Error -->
        <div class="error-messages" [hidden]="!(propertySelectionTouched && (!propertySelectionState || !propertySelectionState.isValid))">
            <p class="text-danger text-center text-md-start mb-0">
                Please select at least one property
            </p>
        </div>
    </div>

    @if (formState() === FormState.Update && !editingProperties() && scheduleData()) {
        <ms-input-label [labelText]="'Properties attached to this invoice schedule'" class="fs-5 pb-1" />

        <ms-charge-schedule-attached-properties
            [propertyIds]="scheduleData()!.propertyIds ?? []"
            [organizationId]="userSessionService.activeOrganization()?.organizationId!"
            (editRequested)="onEditPropertiesRequested()">
        </ms-charge-schedule-attached-properties>
    } @else {
        <ms-input-label [isRequired]="true" [labelText]="'Select Properties to charge'" class="fs-5 pb-1" />

        <div class="accordion mb-3" id="selectedPropertiesAccordion">
            <div class="accordion-item">
                <h2 class="accordion-header" id="selectedPropertiesAccordionHeader">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse"
                        [class.collapsed]="!isPropertySelectionExpanded()"
                        data-bs-target="#selectedPropertiesAccordionCollapse" [attr.aria-expanded]="isPropertySelectionExpanded()"
                        aria-controls="selectedPropertiesAccordionCollapse"
                        (click)="togglePropertySelectionExpanded()">
                        <span class="flex-shrink-0 pe-3">Selected:</span>
                        @if (!isPropertySelectionExpanded()) {
                            @if ((propertySelectionState?.selectedCount ?? 0) > 0) {
                                <span class="badge bg-primary rounded-pill flex-shrink-0 me-3">{{ (propertySelectionState?.selectedCount ?? 0) | number }}</span>
                            }
                            @if (collapsedPropertySelectionSummary) {
                                <span class="text-truncate flex-grow-1 min-w-0 fw-normal">{{ collapsedPropertySelectionSummary }}</span>
                            }
                        }
                    </button>
                </h2>
                <div id="selectedPropertiesAccordionCollapse" class="accordion-collapse collapse"
                    [class.show]="isPropertySelectionExpanded()"
                    aria-labelledby="selectedPropertiesAccordionHeader">
                    <div class="accordion-body selected-recipients-panel">
                        <h6 class="mb-2">
                            Selected
                            <span class="badge bg-primary ms-1">{{ (propertySelectionState?.selectedCount ?? 0) | number }}</span>
                        </h6>

                        @if (propertySelectionState?.useFilter) {
                            <p class="mb-2">
                                <fa-duotone-icon icon="circle-check" class="text-primary me-2" secondaryOpacity="0.9" />
                                All {{ propertySelectionState?.selectedCount | number }} properties matching the current filter.
                            </p>
                            @if ((propertySelectionState?.selectedItems?.length ?? 0) > 0) {
                                <p class="text-muted small mb-2">Excluded (click to re-include):</p>
                                <div class="d-flex flex-wrap">
                                    @for (property of propertySelectionState!.selectedItems; track property.id) {
                                        <button type="button"
                                            class="tag-filter btn btn-ms-secondary"
                                            (click)="propertySelectorComponent.deselect(property.id)"
                                            [attr.aria-label]="'Re-include ' + property.label">
                                            {{ property.label }}
                                            <fa-duotone-icon icon="plus" secondaryOpacity="0.9" />
                                        </button>
                                    }
                                </div>
                            }
                        } @else if ((propertySelectionState?.selectedItems?.length ?? 0) === 0) {
                            <p class="text-muted mb-0">No properties selected yet.</p>
                        } @else {
                            <div class="d-flex flex-wrap">
                                @for (property of propertySelectionState!.selectedItems; track property.id) {
                                    <button type="button"
                                        class="tag-filter btn btn-primary"
                                        (click)="propertySelectorComponent.deselect(property.id)"
                                        [attr.aria-label]="'Remove ' + property.label">
                                        {{ property.label }}
                                        <fa-duotone-icon icon="minus" secondaryOpacity="0.9" />
                                    </button>
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>

        <ms-charge-schedule-property-selector
            #propertySelectorRef
            [organizationId]="userSessionService.activeOrganization()?.organizationId!"
            [initialFilter]="propertiesFilter"
            (selectionChanged)="onPropertySelectionChanged($event)">
        </ms-charge-schedule-property-selector>
    }
</form>

<ms-confirmation-modal #chargeScheduleConfirmationModalRef modalId="chargeScheduleConfirmationModal"
    confirmButtonText="Leave" cancelButtonText="Stay"
    (confirmed)="confirmationConfirmFunction()"
    (cancelled)="confirmationCancelFunction()"
/>

<ms-confirmation-modal #chargeScheduleSaveConfirmationModalRef modalId="chargeScheduleSaveConfirmationModal"
    [enableNotes]="true"
    [isNoteRequired]="false"
    confirmButtonText="Save"
    cancelButtonText="Cancel"
    (confirmed)="onSaveConfirmed($event)"
    (cancelled)="onSaveCancelled()"
/>

<ms-confirmation-modal #editPropertiesConfirmationModalRef modalId="chargeScheduleEditPropertiesConfirmationModal"
    confirmButtonText="Continue"
    cancelButtonText="Cancel"
    (confirmed)="onEditPropertiesConfirmed()"
    (cancelled)="onEditPropertiesCancelled()"
/>

<ms-info-modal #chargeScheduleInfoModalRef modalId="chargeScheduleInfoModal" (onConfirm)="infoModalConfirmed()" />